import type { FC } from "react";
import { useState } from "react";
import { MRTGrid } from "@foodpilot/foods";
import { CompanyGrid as CompanyGridType } from "src/api/client-api/company/useGetAllOrganization";
import { useCompanyListContext } from "./CompanyListContext";
import { useTranslation } from "react-i18next";
import { Box, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useDeleteOrganization } from "src/api/client-api/company/useDeleteOrganization";
import { useArchiveSupplier } from "src/api/client-api/company/useArchiveSupplier";
import { getSupplierKey } from "src/api/client-api/company/useGetSupplier";
import { suppliersKey } from "src/api/client-api/company/useGetSuppliers";
import { useSnackbar } from "src/utils/useSnackbar";
import { useQueryClient } from "react-query";
import { CompanyModal } from "./CompanyModal";
import { DELETE, MODIFY, ORGANIZATION, useFoodpilotContext } from "src/context/FoodpilotContext";
import { canAdministerSuppliers } from "../Supplier/supplierAdministration";

export const CompanyGrid: FC = () => {
    const { t } = useTranslation();
    const {
        companies,
        isLoading,
        mrt_columns,
        totalItems,
        companyPage,
        setCompanyPage,
        sorting,
        setSorting,
        delayedSearchCompany,
        isProvider,
    } = useCompanyListContext();
    const deleteOrganization = useDeleteOrganization();
    const archiveSupplier = useArchiveSupplier();
    const deleteCompany = isProvider ? archiveSupplier : deleteOrganization;
    const listKey = isProvider ? suppliersKey : "getAllCompany";
    const detailKey = isProvider ? getSupplierKey : "getOneCompany";
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const { canUser, user } = useFoodpilotContext();
    const canModifyOrganization =
        isProvider ? canAdministerSuppliers(user.trustedUser, MODIFY) : canUser(MODIFY, ORGANIZATION);
    const canDeleteOrganization =
        isProvider ? canAdministerSuppliers(user.trustedUser, DELETE) : canUser(DELETE, ORGANIZATION);
    const canManageOrganization = canModifyOrganization || canDeleteOrganization;

    const [selectedCompany, setSelectedCompany] = useState<CompanyGridType | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<CompanyGridType | null>(null);

    const handleEditClick = (company: CompanyGridType) => {
        if (!canModifyOrganization) {
            return;
        }

        setSelectedCompany(company);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (company: CompanyGridType) => {
        if (!canDeleteOrganization || company.isPlatformOwner) {
            return;
        }

        setCompanyToDelete(company);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (companyToDelete && canDeleteOrganization) {
            deleteCompany.mutate(companyToDelete.id, {
                onSuccess: () => {
                    queryClient.invalidateQueries([listKey], { exact: false });
                    setIsDeleteDialogOpen(false);
                    setCompanyToDelete(null);
                    snackbar.setSnackbarProps({
                        message: t("delete"),
                        type: "success",
                    });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        message: t("unknown_error"),
                        type: "error",
                    });
                },
            });
        }
    };

    return (
        <>
            <div style={{ backgroundColor: "#FFFFFF", borderRadius: "12px", overflow: "hidden" }}>
                <MRTGrid
                    data={companies || []}
                    columns={mrt_columns.mrt_columns}
                    enableRowActions={canManageOrganization}
                    enableRowSelection={false}
                    enablePagination={true}
                    enableSelectAll={false}
                    enableColumnPinning={true}
                    enableStickyFooter={true}
                    displayColumnDefOptions={{
                        "mrt-row-actions": {
                            size: 120,
                            grow: false,
                        },
                    }}
                    renderRowActions={({ row }) => (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {canModifyOrganization && (
                                <Box
                                    component="button"
                                    aria-label={t("common.action.edit", "Edit")}
                                    onClick={() => handleEditClick(row.original)}
                                    sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s",
                                        "&:hover": {
                                            backgroundColor: "#F5F5F5",
                                        },
                                    }}
                                >
                                    <FoodsIcon icon="edit" size={1} />
                                </Box>
                            )}
                            {canDeleteOrganization && (
                                <Box
                                    component="button"
                                    aria-label={t("delete")}
                                    onClick={() => handleDeleteClick(row.original)}
                                    sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: row.original.isPlatformOwner ? "not-allowed" : "pointer",
                                        transition: "background-color 0.2s",
                                        "&:hover": {
                                            backgroundColor: row.original.isPlatformOwner ? "transparent" : "#F5F5F5",
                                        },
                                    }}
                                    disabled={row.original.isPlatformOwner}
                                >
                                    <FoodsIcon icon="delete" size={1} />
                                </Box>
                            )}
                        </Stack>
                    )}
                    state={{
                        isLoading: isLoading,
                        pagination: companyPage,
                        sorting: sorting,
                        globalFilter: delayedSearchCompany,
                        columnVisibility: mrt_columns.columnVisibility,
                        columnOrder: mrt_columns.columnOrder,
                        columnSizing: mrt_columns.columnSizing,
                        columnPinning: {
                            left: [mrt_columns.columnOrder[0]],
                            right: ["mrt-row-actions"],
                        },
                    }}
                    onColumnSizingChange={mrt_columns.setColumnSizing}
                    // Server-side pagination
                    rowCount={totalItems}
                    manualPagination={true}
                    onPaginationChange={setCompanyPage}
                    manualSorting={true}
                    onSortingChange={setSorting}
                    manualFiltering={true}
                    localization={{
                        actions: "",
                    }}
                />
            </div>

            {canModifyOrganization && (
                <CompanyModal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    company={selectedCompany || undefined}
                    onSuccess={() => {
                        queryClient.invalidateQueries([listKey], { exact: false });
                        queryClient.invalidateQueries([detailKey], { exact: false });
                        // Remove cache for this specific company to force reload on next open
                        if (selectedCompany?.id) {
                            queryClient.removeQueries([detailKey, selectedCompany.id.toString()]);
                        }
                        setIsModalOpen(false);
                        snackbar.setSnackbarProps({
                            message:
                                selectedCompany ?
                                    t("company.updateSuccess", "Company updated successfully")
                                :   t("company.createSuccess", "Company created successfully"),
                            type: "success",
                        });
                    }}
                    isProvider={isProvider === true}
                />
            )}

            {canDeleteOrganization && (
                <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                    <DialogTitle>{t("supprimer")}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t("common.areyousure")} "{companyToDelete?.name}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsDeleteDialogOpen(false)}>{t("cancel")}</Button>
                        <Button onClick={handleDeleteConfirm} color="error">
                            {t("delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};
